import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import '../css/advancedProducts.css';

// Computed status configuration
const STATUS_CONFIG = {
    'Active':       { className: 'status-active' },
    'Discontinued': { className: 'status-discontinued' },
    'Arriving':     { className: 'status-arriving' },
    'Delayed':      { className: 'status-delayed' },
    'Out of Stock': { className: 'status-out-of-stock' },
};

/**
 * Compute product status from real data fields.
 * Priority order:
 * 1. DISC + sellable > 0 → Active
 * 2. DISC + sellable = 0 → Discontinued
 * 3. sellable > 0 → Active
 * 4. sellable = 0, future incoming_date → Arriving [date]
 * 5. sellable = 0, past incoming_date → Delayed
 * 6. sellable = 0, no incoming_date → Out of Stock
 *
 * @param {object} data - Row data from productSearch
 * @returns {{ status: string, label: string }}
 */
export function computeProductStatus(data) {
    const ecommOnhand = parseInt(data?.ecomm_onhand, 10) || 0;
    const productFactor = parseInt(data?.productFactor, 10) || 1;
    const sellable = Math.floor(ecommOnhand / productFactor);
    const isDisc = (data?.ibmi_size || '').toUpperCase() === 'DISC';
    const incomingDateRaw = data?.ibmi_incoming_date;

    // Rules 1 & 2: Discontinued flag
    if (isDisc) {
        if (sellable > 0) return { status: 'Active', label: 'Active' };
        return { status: 'Discontinued', label: 'Discontinued' };
    }

    // Rule 3: Has sellable inventory
    if (sellable > 0) return { status: 'Active', label: 'Active' };

    // Rules 4-6: Out of stock — check incoming date
    if (incomingDateRaw) {
        // Parse YYYYMMDD format
        const raw = String(incomingDateRaw).replace(/[^0-9]/g, '');
        if (raw.length === 8) {
            const year = parseInt(raw.substring(0, 4), 10);
            const month = parseInt(raw.substring(4, 6), 10) - 1;
            const day = parseInt(raw.substring(6, 8), 10);
            const incomingDate = new Date(year, month, day);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (incomingDate >= today) {
                const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const label = `Arriving ${monthNames[month]} ${day}`;
                return { status: 'Arriving', label };
            }
            return { status: 'Delayed', label: 'Delayed' };
        }
    }

    return { status: 'Out of Stock', label: 'Out of Stock' };
}

// Cell Renderer - displays the computed badge (non-editable)
export const StatusBadgeRenderer = (props) => {
    const { value } = props;
    if (!value) return null;

    // Match "Arriving*" prefix to look up config
    const configKey = value.startsWith('Arriving') ? 'Arriving' : value;
    const config = STATUS_CONFIG[configKey] || STATUS_CONFIG['Out of Stock'];

    return (
        <div
            className={`status-badge ${config.className}`}
            style={{ userSelect: 'none', cursor: 'default' }}
        >
            {value}
        </div>
    );
};

// Category/Vendor Badge Renderer
export const CategoryBadgeRenderer = (props) => {
    const { value } = props;
    
    if (!value) {
        return <span style={{ color: '#9da9bb', fontStyle: 'italic' }}>—</span>;
    }
    
    return (
        <div className="category-badge">
            {value}
            <span className="dropdown-icon">▼</span>
        </div>
    );
};

// Generic Select Cell Editor for categories, vendors, etc.
export const SelectCellEditor = forwardRef((props, ref) => {
    const { value, values = [], stopEditing, colDef } = props;
    const [selectedValue, setSelectedValue] = useState(value || '');
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Get options from colDef.cellEditorParams.values or props.values
    const options = colDef?.cellEditorParams?.values || values || [];

    useImperativeHandle(ref, () => ({
        getValue: () => selectedValue,
        isPopup: () => true,
    }));

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const filteredOptions = options.filter(opt => 
        opt.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (val) => {
        setSelectedValue(val);
        setTimeout(() => stopEditing(), 50);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            stopEditing(true);
        } else if (e.key === 'Enter' && filteredOptions.length > 0) {
            handleSelect(filteredOptions[0]);
        }
    };

    return (
        <div 
            ref={containerRef}
            style={{ 
                background: 'white',
                border: '1px solid #e3e6ea',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                minWidth: '180px',
                maxHeight: '250px',
                overflow: 'hidden'
            }}
        >
            <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search..."
                style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    borderBottom: '1px solid #e3e6ea',
                    outline: 'none',
                    fontSize: '13px'
                }}
            />
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {filteredOptions.map((opt, idx) => (
                    <div
                        key={idx}
                        onClick={() => handleSelect(opt)}
                        style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            backgroundColor: selectedValue === opt ? '#e8f4fd' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = selectedValue === opt ? '#e8f4fd' : 'transparent'}
                    >
                        {opt}
                    </div>
                ))}
                {filteredOptions.length === 0 && (
                    <div style={{ padding: '12px', color: '#9da9bb', textAlign: 'center' }}>
                        No options found
                    </div>
                )}
            </div>
        </div>
    );
});

SelectCellEditor.displayName = 'SelectCellEditor';

export default StatusBadgeRenderer;
